import { useRef } from 'react';
import { m, useInView } from 'framer-motion';
import { varAlpha } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { CONFIG } from 'src/global-config';
import { useLocale } from 'src/locales';
import { Button } from '@mui/material';
import { Iconify } from 'src/components/iconify';
import { useRouter } from 'next/navigation';

// ----------------------------------------------------------------------

const COLORS = ['primary', 'secondary', 'warning', 'success'];

const SUMMARY = [
  { title: 'Years of experience', total: 12, icon: 'carbon:increase-level' },
  { title: 'Awards', total: 20, icon: 'carbon:trophy' },
  { title: 'Projects', total: 150, icon: 'carbon:data-vis-4' },
  { title: 'Happy clients', total: 32000, icon: 'carbon:user-certification' },
];

// ----------------------------------------------------------------------

export function Solutions({ sx, ...other }) {
  const { t } = useLocale();

  const ref = useRef(null);
  const { push } = useRouter();

  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const bulletPoints = [
    { icon: 'hugeicons:contracts', label: 'Long_Term_Transport_Contracts' },
    { icon: 'hugeicons:task-daily-02', label: 'Daily_Monthly_Truck_Leasing' },
    { icon: 'ph:shipping-container-light', label: 'Port_Container_Transport' },
    { icon: 'stash:integrations', label: 'System_Integration_SAP' },
    { icon: 'gis:road-map', label: 'Cross_Border_Land_Transport' },
  ];
  const language = localStorage.getItem('i18nextLng') || 'ar';
  const isRTL = language === 'ar';

  return (
    <Box
      component={m.section}
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      sx={[
        {
          py: { xs: 5, md: 10 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container>
        <Grid container spacing={3} sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Image Section */}
          <Grid size={{ xs: 12, md: 6, lg: 5 }}>
            <m.div
              initial={{ opacity: 0, x: -50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <Box
                component="img"
                alt={t('home-img-title')}
                title={t('home-img-meta-desk')}
                src={`${CONFIG.assetsDir}/assets/images/home/seo/Home/shahen-integrated-business-solutions.webp`}
                sx={{
                  display: 'block',
                  width: '100%',
                  height: 480,
                  borderRadius: 2,
                  maxWidth: { sm: 400, md: 480 },
                  mx: 'auto',
                  maxHeight: { xs: 200, sm: 300, md: 480 },
                  objectFit: 'cover',
                }}
              />
            </m.div>
          </Grid>

          {/* Text Section */}
          <Grid sx={{ textAlign: { xs: 'center', md: 'left' } }} size={{ xs: 12, md: 6, lg: 6 }}>
            <m.div
              initial={{ opacity: 0, x: 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <Typography variant="h3" sx={{ fontSize: '2rem !important' }}>
                {t('Integrated_Solutions_to_Support_Your_Business_Growth')}
              </Typography>

              <Typography sx={{ mt: 3, mb: 5, color: 'text.secondary' }}>
                {t('Integrated_Solutions_to_Support_Your_Business_Growth_desc')}
              </Typography>

              {/* Bullet list */}
              <Box
                component="ul"
                sx={{
                  mb: 5,
                  gap: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  color: 'text.secondary',
                  textAlign: 'left',
                  // listStyleType: 'disc',
                  '& li::marker': {
                    color: 'green',
                    fontSize: '1.1rem',
                  },
                }}
              >
                {bulletPoints?.map((item, index) => (
                  <m.li
                    key={index}
                    initial={{ opacity: 0, x: 30 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      listStyle: 'none',
                      fontSize: 18,
                    }}
                  >
                    <Iconify width={24} icon={item.icon} />

                    {t(item.label)}
                  </m.li>
                ))}
              </Box>
            </m.div>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                push('/business');
              }}
              endIcon={<Iconify icon={isRTL ? 'carbon:chevron-left' : 'carbon:chevron-right'} />}
            >
              {t('Explore_All_Our_Solutions')}
            </Button>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

const IconWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'deg' && prop !== 'color',
})(({ color, deg, theme }) => ({
  width: 160,
  height: 160,
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  position: 'relative',
  justifyContent: 'center',
  color: theme.vars.palette[color].darker,
  border: `dashed 2px ${varAlpha(theme.vars.palette[color].mainChannel, 0.24)}`,
  '&:before': {
    zIndex: 8,
    content: '""',
    borderRadius: '50%',
    position: 'absolute',
    width: 'calc(100% - 48px)',
    height: 'calc(100% - 48px)',
    background: `conic-gradient(from ${deg}deg at 50% 50%, ${theme.vars.palette[color].main} 0deg, ${theme.vars.palette[color].light} 360deg)`,
  },
  '& svg': { zIndex: 9 },
}));
